<template>
  <div :class="rootClass" :style="rootStyle" :id="rendering?.dataSource">
    <ContentGrid :desktop-default-grid="[2, 20]" :mobile-default-grid="[1, 14]">
      <div class="webinars-detail-tags">
        <JssRichText
          v-if="new Date() < new Date(fields?.webinarsDate?.value)"
          class="webinars-detail-tag"
          :field="fields?.webinarsTag"
        />
        <JssRichText
          class="webinars-detail-tag"
          :class="fields?.webinarsCategory?.fields?.phrase?.value"
          :field="fields?.webinarsCategory?.fields?.phrase"
        />
      </div>
      <JssRichText class="webinars-detail-title" :field="fields?.webinarsTitle" tag="h1" />
      <div class="webinars-detail-share">
        <div class="webinars-detail-share-lt">
          <div class="webinars-detail-share-lt-item">
            <div
              class="webinars-detail-share-lt-item-content"
              v-if="fields?.webinarsDate?.value !== '0001-01-01T00:00:00Z'"
            >
              <Icon name="date" />
              <span class="webinars-detail-share-lt-item-desc">{{
                formatWebinarsDate(fields)
              }}</span>
            </div>
            <div class="webinars-detail-share-lt-item-content" v-if="fields?.webinarsTime?.value">
              <Icon name="time" />
              <span class="webinars-detail-share-lt-item-desc">{{
                fields?.webinarsTime?.value
              }}</span>
            </div>
          </div>
        </div>
      </div>
      <AdaptiveImage
        class="webinars-detail-bg"
        :field="fields?.webinarsDetailBg"
        :isBackground="false"
        :lazy-load="false"
        :enableScrollDetection="false"
        :show="true"
      />
      <JssRichText class="webinars-detail-body" :field="fields?.webinarsBody" tag="div" />
    </ContentGrid>
  </div>
</template>

<script>
import utils from '@/utils';

export default {
  name: 'WebinarsDetail',
  props: {
    fields: {
      type: Object,
      default: () => ({}),
    },
    rendering: {
      type: Object,
    },
    page: {
      type: Object,
      default: () => ({}),
    },
  },
  setup(props) {
    const rootClass = utils.overall.baseComClass(props.fields, 'webinars-detail');

    const rootStyle = utils.overall.baseComStyle(props.fields);

    const formatWebinarsDate = (item) => {
      const date = item?.webinarsDate?.value;
      const formattedDate = utils.date.formatDateWithOrdinal(date);
      if (formattedDate) {
        return `${formattedDate}`;
      }
      return formattedDate || '';
    };

    const formatWebinarsDateTime = (item) => {
      const date = item?.fields?.webinarsDate?.value;
      const time = item?.fields?.webinarsTime?.value;
      const formattedDate = utils.date.formatDateWithOrdinal(date);
      if (formattedDate && time) {
        return `${formattedDate} ${time}`;
      }
      return formattedDate || time || '';
    };

    return {
      rootClass,
      rootStyle,
      formatWebinarsDate,
      formatWebinarsDateTime,
    };
  },
};
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.webinars-detail {
  position: relative;
  width: 100%;
  padding: 40px 0;

  &-tags {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  &-tag {
    font-size: 12px;
    color: $blue-night;
    padding: 0 6px;
    height: 20px;
    line-height: 20px;
    background: $blue-night;
    border-radius: 4px;
    display: inline-block;
    color: $white;

    &.forum {
      color: $blue-night;
      background: $grey-neutral;
    }
    &.conference {
      color: $grey-black;
      background: $neutral-disable-surface;
    }
    &.symposium {
      color: #e9550d;
      background: rgba(239, 85, 10, 0.2);
    }
  }

  &-title {
    margin-top: 16px;
    font-size: 16px;
  }

  &-share {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 16px;

    &-lt {
      display: flex;
      align-items: center;
      gap: 16px;

      &-item {
        display: flex;
        align-items: center;
        gap: 8px;

        svg {
          fill: transparent;
        }

        &-desc {
          font-size: 14px;
          line-height: 1.4;
          color: #656565;
        }

        &-content {
          display: flex;
          align-items: center;
          gap: 4px;
        }
      }
    }

    &-rt {
      display: flex;
      align-items: center;

      &-share {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        justify-self: end;
        width: 40px;
        height: 40px;
        padding: 0;
        color: #111111;
        background: transparent;
        border: 0;
        cursor: pointer;

        svg {
          width: 32px;
          height: 32px;
        }

        &:hover,
        &:focus-visible {
          color: #739b78;
          outline: none;
        }
      }

      &-button {
        margin-left: 16px;
      }
    }
  }

  &-bg {
    margin-top: 24px;
    height: auto;

    .e-background-image__content {
      padding: 20px;
      display: flex;
      flex-direction: column;
    }

    &-online {
      font-size: 14px;
      line-height: 1.4;
    }

    &-desc {
      font-size: 20px;
      font-weight: 700;
      line-height: 1.4;
      margin-top: 8px;
      @include line-clamp(2);
    }

    &-date {
      font-size: 14px;
      line-height: 1.4;
      margin-top: 8px;
    }
  }

  &-body {
    font-size: 16px;
    line-height: 1.4;
    margin-top: 24px;
    img {
      max-width: 100% !important;
    }
    p {
      font-size: 16px;
    }
    h2 {
      font-size: 22px;
      color: $blue-night;
      margin: 16px 0;
    }
    a {
      color: $blue-night;
    }
  }

  @include tablet-landscape {
    padding: 60px 0;

    &-title {
      margin-top: 24px;
      font-size: 32px;
    }

    &-share {
      margin-top: 30px;
    }

    &-body {
      margin-top: 40px;
    }

    &-bg {
      .e-background-image__content {
        padding: 30px;
      }

      &-online {
        font-size: 24px;
      }

      &-desc {
        font-size: 32px;
        margin-top: 24px;
      }

      &-date {
        font-size: 24px;
        margin-top: 24px;
      }
    }
  }
}
</style>
